import { useState, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for IDComply verification operations
 */
export const useIdComply = () => {
  const { toast } = useToast();
  const [isVerifying, setIsVerifying] = useState(false);

  /**
   * Performs data verification for a user
   */
  const performDataVerification = useCallback(async (profileData: {
    firstName: string;
    lastName: string;
    dateOfBirth: Date | string;
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phoneNumber?: string;
    email?: string;
    addressLine2?: string;
  }) => {
    setIsVerifying(true);
    try {
      const dateOfBirth = typeof profileData.dateOfBirth === 'string' 
        ? profileData.dateOfBirth 
        : profileData.dateOfBirth.toISOString().split('T')[0];

      const response = await apiClient.post('/idcomply/data-verification', {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        dateOfBirth,
        streetAddress: profileData.streetAddress,
        city: profileData.city,
        state: profileData.state,
        zip: profileData.zip,
        country: profileData.country,
        phoneNumber: profileData.phoneNumber,
        email: profileData.email,
      });

      if (!response.success) {
        // Get user-friendly error message from response
        const errorMessage = response.message || response.error || 'Data verification failed';
        throw new Error(errorMessage);
      }

      return response;
    } catch (error: any) {
      console.error('Data verification error:', error);
      
      // Extract the most user-friendly error message available
      let errorMessage = 'Failed to verify your information. Please try again.';
      
      if (error.response?.message || error.response?.data?.message) {
        errorMessage = (error.response?.message ?? error.response?.data?.message) as string;
      } else if (error.response?.error && typeof error.response.error === 'string') {
        // Convert error codes to user-friendly messages
        const errorCode = error.response.error;
        const errorMessages: Record<string, string> = {
          'VERIFICATION_FAILED': 'We couldn\'t verify your information. Please check your details and try again.',
          'INVALID_DATA': 'The information provided is invalid. Please check your details.',
          'SERVICE_UNAVAILABLE': 'The verification service is temporarily unavailable. Please try again later.',
          'MAX_ATTEMPTS_EXCEEDED': 'You have exceeded the maximum number of verification attempts. Please contact support.',
          'INTERNAL_ERROR': 'An unexpected error occurred. Please try again later.',
        };
        errorMessage = errorMessages[errorCode] || errorCode;
      } else if (error.message && !error.message.startsWith('HTTP')) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Verification Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 6000,
      });
      throw error;
    } finally {
      setIsVerifying(false);
    }
  }, [toast]);

  /**
   * Creates a token for ID photo verification
   */
  const createIdVerificationToken = useCallback(async (redirectUrl?: string) => {
    setIsVerifying(true);
    try {
      const response = await apiClient.post('/idcomply/id-verification/token', {
        redirectUrl,
      });

      if (!response.success) {
        const errorMessage = response.message || response.error || 'Failed to create verification token';
        throw new Error(errorMessage);
      }

      return response;
    } catch (error: any) {
      console.error('ID verification token creation error:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Failed to create verification link. Please try again.';
      
      if (error.response?.message) {
        errorMessage = error.response.message;
      } else if (error.message && !error.message.startsWith('HTTP')) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Verification Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 6000,
      });
      throw error;
    } finally {
      setIsVerifying(false);
    }
  }, [toast]);

  /**
   * Gets the current verification status for the user
   */
  const getVerificationStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/idcomply/verification-status');
      return response;
    } catch (error: any) {
      console.error('Error getting verification status:', error);
      return null;
    }
  }, []);

  return {
    performDataVerification,
    createIdVerificationToken,
    getVerificationStatus,
    isVerifying,
  };
};

